import React from "react";
import { Card, Row, Col, Typography, Skeleton, Table, List, Progress, Tooltip as AntTooltip, Space } from 'antd';
import {
    CloudUploadOutlined,
    CloudDownloadOutlined,   
    RightCircleTwoTone,
    DownCircleTwoTone
} from '@ant-design/icons';

import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';

import { getApplicationUsage } from '../../api/application';
import {
    useGetData,
    //useGetDataFromCache
} from "../../hooks";

import { humanFileSize } from "../../services/utils"

interface DataTransferredType {
    total: number;
    download: number;
    upload: number;
}

interface ApplicationUsageType {
    applicationName?: string;
    dataTransferredInBytes?: number;
    kind?: string;
}

interface TransformDataArgs {
    applicationCategory?: string;
    applicationUsage?: Array<ApplicationUsageType> | null;
    downstreamDataTransferredDuringLast24HoursInBytes: number;
    isBlockable?: boolean;
    isBlocked?: boolean;
    kind?: string;
    networkId?: string;
    networkSsid?: string;
    upstreamDataTransferredDuringLast24HoursInBytes: number;
    siteName: string;
}

interface TransformDataBySites {
    [key: string]: Array<TransformDataArgs>
}

interface UsageByAppsType {
    [key: string]: {
        applicationUsage: ApplicationUsageType[],
        totalUsageInBytes: number,
        totalUsageInPercent: number
    }
}

interface AppUsageDataType {
    category: string;
    apps: ApplicationUsageType[];
    usageInNumber: string;
    usageInPercent: number;
}

interface UsageByCateForNetworkType {
    [category: string]: Array<TransformDataArgs>
}

const dynamicColors = () => {
    var r = Math.floor(Math.random() * 255);
    var g = Math.floor(Math.random() * 255);
    var b = Math.floor(Math.random() * 255);
    return "rgb(" + r + "," + g + "," + b + ")";
};

const toGb = (bytesValue: any) => parseFloat((parseInt(bytesValue) / (1024 * 1024 * 1024)).toFixed(5))

const transformData = (data: Array<TransformDataBySites>): { 
    dataLast24Hours: any; 
    mostUsed: any; 
    usageByApp: UsageByAppsType | null;
    usageByCateForNetwork: UsageByCateForNetworkType | null;
} => {    
    if (data.length <= 0) {
        return {
            dataLast24Hours: null,
            mostUsed: null,
            usageByApp: null,
            usageByCateForNetwork: null
        };
    }

    let combinedData: Array<TransformDataArgs> = [];

    data.forEach(d => {
        for (const [key, value] of Object.entries(d)) {
            const valueWithSiteName = value.map( item => {
                (item as any)['siteName'] = key;
                (item as any)['totalBytesInHuman'] = humanFileSize(item.downstreamDataTransferredDuringLast24HoursInBytes + item.upstreamDataTransferredDuringLast24HoursInBytes)
                return item;
            } )
            combinedData = combinedData.concat(valueWithSiteName)
        }
    })

    let dataLast24Hours: any = {}, mostUsed: any = {};
    let usageByApp: UsageByAppsType = {}
    let usageByCateForNetwork: UsageByCateForNetworkType = {}
    let download = 0, upload = 0;

    const setOfCategories = [...new Set(combinedData.map(item => item.applicationCategory))];

    setOfCategories.forEach(category => {
        if (category) {
            Object.assign(mostUsed, {
                [category]: {
                    total: 0,
                    download: 0,
                    upload: 0,
                }
            });

            Object.assign<any, UsageByAppsType>(
                usageByApp, {
                    [category]: {
                        applicationUsage: [],
                        totalUsageInBytes: 0,
                        totalUsageInPercent: 0
                    }
                }
            );
            
            for (let i = 0; i < combinedData.length; i++) {
                if (combinedData[i].applicationCategory && combinedData[i].applicationCategory === category) {
                    mostUsed[category].download += combinedData[i].downstreamDataTransferredDuringLast24HoursInBytes;
                    mostUsed[category].upload += combinedData[i].upstreamDataTransferredDuringLast24HoursInBytes
                    mostUsed[category].total += combinedData[i].downstreamDataTransferredDuringLast24HoursInBytes + combinedData[i].upstreamDataTransferredDuringLast24HoursInBytes;
                    
                    combinedData[i].applicationUsage?.forEach((app) => {                        
                        const apps = usageByApp[category].applicationUsage
                        const idx = apps.findIndex(a => a.applicationName === app.applicationName);
                        if (idx !== -1) {
                            let data = apps[idx].dataTransferredInBytes
                            if ( data && app.dataTransferredInBytes) {
                                apps[idx].dataTransferredInBytes = data + app.dataTransferredInBytes
                            }                            
                        } else {
                            apps.push(app)
                        }                        
                    })                    

                    // usageByApp[category].applicationUsage
                    download += combinedData[i].downstreamDataTransferredDuringLast24HoursInBytes;
                    upload += combinedData[i].upstreamDataTransferredDuringLast24HoursInBytes;    
                    
                    // const aa = combinedData[i].applicationUsage
                    // if (aa) {
                    //     const zidx = aa.findIndex(a => a.applicationName === "zalo")
                    //     if (zidx && zidx !== -1) {
                    //         console.log(`${combinedData[i].networkSsid}: ${aa[zidx].applicationName}: ${aa[zidx].dataTransferredInBytes}`)
                    //     }                    
                    // }
                    
                }
                                
            }                                                
        }
    });

    setOfCategories.forEach(cate => {
        if (cate) {
            let nnn = combinedData
                .filter(item => item.applicationCategory === cate)
                .sort((a, b) =>                      
                    (b.downstreamDataTransferredDuringLast24HoursInBytes + b.upstreamDataTransferredDuringLast24HoursInBytes) -
                    (a.downstreamDataTransferredDuringLast24HoursInBytes + a.upstreamDataTransferredDuringLast24HoursInBytes)
                ).slice(0, 5)
                .filter(item => (item.downstreamDataTransferredDuringLast24HoursInBytes + item.upstreamDataTransferredDuringLast24HoursInBytes) > 0);

            usageByCateForNetwork[cate] = nnn;
        }  
    })

    const totalBytes = download + upload;

    setOfCategories.forEach(category => {
        if (category) {
            if (category === "wired") {
                usageByApp["wired"].totalUsageInBytes = mostUsed["wired"].total
                usageByApp["wired"].totalUsageInPercent = mostUsed["wired"].total ? parseFloat(((toGb(mostUsed["wired"].total) / toGb(totalBytes)) * 100).toFixed(2)) : 0;
            }
            else {
                usageByApp[category].applicationUsage.forEach(usage => {
                    if (usage.dataTransferredInBytes) {
                        usageByApp[category].totalUsageInBytes += usage.dataTransferredInBytes;                    
                    }
                })
    
                const tPercent = (toGb(usageByApp[category].totalUsageInBytes) / toGb(totalBytes) ) * 100
                let toFix = 1;
                if (tPercent * 100 >= 1) {
                    toFix = 2;
                }
                else if (tPercent * 1000 >= 1) {
                    toFix = 3;
                }
                else {
                    toFix = 4;
                }
                usageByApp[category].totalUsageInPercent = parseFloat(tPercent.toFixed(toFix))
            }
        }        
    });

    Object.assign(dataLast24Hours, {
        download,
        upload,
        total: totalBytes
    });

    // setOfCategories.forEach(cate => {
    //     if (cate) {
    //         usageByApp[cate].applicationUsage.forEach(usage => {
    //             if (usage.dataTransferredInBytes) {
    //                 usageByApp[cate].totalUsageInBytes += usage.dataTransferredInBytes;                    
    //             }
    //         })
            
    //         if (cate === "wired") {
    //             usageByApp["wired"].totalUsageInBytes = mostUsed["wired"].total                
    //         }
    //     }        
    // })

    // setOfCategories.forEach(cate => {
    //     if (cate) {
    //         // const tPercent = parseFloat(((toGb(usage.dataTransferredInBytes) / toGb(totalBytes) ) * 100).toFixed(2))
    //         //         console.log(`percent of ${cate}: ${tPercent}`)
    //         //         Object.assign(usageByApp, {
    //         //             [cate]: {
    //         //                 ...usageByApp[cate],
    //         //                 'totalUsageInPercent': tPercent
    //         //             }
    //         //         })

    //         const tPercent = (toGb(usageByApp[cate].totalUsageInBytes) / toGb(totalBytes) ) * 100
    //         let toFix = 1;
    //         if (tPercent * 100 >= 1) {
    //             toFix = 2;
    //         }
    //         else if (tPercent * 1000 >= 1) {
    //             toFix = 3;
    //         }
    //         else {
    //             toFix = 4;
    //         }
    //         usageByApp[cate].totalUsageInPercent = parseFloat(tPercent.toFixed(toFix))

    //         if (cate === "wired") {
    //             usageByApp["wired"].totalUsageInPercent = mostUsed["wired"].total ? parseFloat(((toGb(mostUsed["wired"].total) / toGb(totalBytes)) * 100).toFixed(2)) : 0;
    //         }
    //     }        
    // })

    console.log('usageByCateForNetwork', usageByCateForNetwork)

    return {
        dataLast24Hours, mostUsed, usageByApp, usageByCateForNetwork
    }

}

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement, 
    Title,
    Tooltip, 
    Legend
);

const ApplicationsPage: React.FC = () => {

    const { data, error, isLoading, isFetching } = useGetData('application', getApplicationUsage);
    //const dashboardData = useGetDataFromCache(["get-data-dashboard"]);
    const [dataTransferred, setDataTransferred] = React.useState<DataTransferredType>();
    // const [mostCategoryUsed, setMostCategoryUsed] = React.useState<Array<any>>();
    const [chartData, setChartData] = React.useState<any>();
    
    const [appUsageData, setAppUsageData] = React.useState<AppUsageDataType[]>([]);

    const [networkUsageByCate, setNetworkUsageByCate] = React.useState<UsageByCateForNetworkType | null>({});


    React.useEffect(() => {
        if (!isLoading && !isFetching && !error && data) { 
            
            // transform data
            let { dataLast24Hours, mostUsed, usageByApp, usageByCateForNetwork } = transformData(data);     
            
            if ( usageByCateForNetwork ) {
                setNetworkUsageByCate(usageByCateForNetwork)
            }

            if (usageByApp) {
                //console.log(usageByApp)
                const appUsageData: AppUsageDataType[] = []
                for (const [k, v] of Object.entries(usageByApp)) {
                    appUsageData.push({
                        'category': k,
                        'apps': v.applicationUsage,
                        'usageInNumber': humanFileSize(v.totalUsageInBytes),
                        'usageInPercent': v.totalUsageInPercent
                    })
                }
                appUsageData.sort( (a: any, b: any) => b.usageInPercent - a.usageInPercent );

                setAppUsageData(appUsageData)
            }
            
            setDataTransferred(dataLast24Hours);

            let mostCateUsed: any = [...Object.entries(mostUsed).sort((a: any, b: any) => b[1].total - a[1].total)];
            // setMostCategoryUsed(mostCateUsed);

            // init colors
            let colors = [], labels: any = [], chartData: any = [];
            for (let i = 0; i < mostCateUsed.length; i++) {
                colors.push(dynamicColors());
            }            

            //labels = [...mostCateUsed.map((cate: any) => { if (cate[0]!=="wired") return cate[0] } )];
            // //data = [...mostCateUsed.map((cate: any) => (toGb(cate[1].total) * 100.0) / toGb(dataLast24Hours.total))]
            // chartData = [...mostCateUsed.map((cate: any) => toGb(cate[1].total))]
            let cateCount = 0;
            for (let i = 0; i < mostCateUsed.length; i++) {
                if (mostCateUsed[i][0] !== "wired") {
                    labels.push(mostCateUsed[i][0])
                    chartData.push(toGb(mostCateUsed[i][1].total))
                    cateCount++;
                }
                if (cateCount > 5) break; 
            } 

            setChartData({
                labels: labels,
                datasets: [
                    {
                        label: 'Data transferred during 24h (GB)',
                        data: chartData,
                        borderColor: 'rgb(255, 131, 0)',
                        backgroundColor: 'rgb(255, 131, 0, 0.5)'
                    }
                ]
            })
        }
    }, [isLoading, isFetching, error, data]);

    return (
        <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
            <Card
                title="Overview"
            >
                <Row gutter={24}>
                    <Col sm={24} md={12} lg={5}>
                        <Typography.Paragraph>
                            <Typography.Title level={4} type="secondary" style={{ margin: 0, paddingBlock: 12 }}>
                                {(isLoading || isFetching) ? (
                                    <>
                                        <Skeleton.Button size="small" active={true} />
                                    </>
                                )
                                    :
                                    humanFileSize(dataTransferred?.total || 0)
                                }
                                <Typography.Text> of data transferred in the last 24 hours</Typography.Text>
                            </Typography.Title>
                        </Typography.Paragraph>
                        <Typography.Paragraph>
                            <CloudDownloadOutlined /> {'Download '}
                            {(isLoading || isFetching) ? (
                                <Skeleton.Button size="small" active />
                            )
                                :
                                humanFileSize(dataTransferred?.download || 0)
                            }
                        </Typography.Paragraph>
                        <Typography.Paragraph>
                            <CloudUploadOutlined /> {'Upload '}
                            {(isLoading || isFetching) ? (
                                <Skeleton.Button size="small" active />
                            )
                                :
                                humanFileSize(dataTransferred?.upload || 0)
                            }
                        </Typography.Paragraph>
                    </Col>                
                    <Col sm={24} md={12} lg={19}>
                    {(chartData !== undefined) && (
                        <Bar                                                         
                            data={chartData}
                            options={{
                                indexAxis: 'y' as const,
                                elements: {
                                    bar: { borderWidth: 2 }
                                },
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: {
                                        position: 'right' as const
                                    },
                                    title: {
                                        display: true,
                                        text: 'Top 5 Most Categories'
                                    }
                                }
                            }}
                        />
                    )}
                    </Col>                    
                </Row>            
            </Card >

            <Card title="Application Usage">
                <Table 
                    rowKey="category"
                    columns={[
                        { title: 'Name', dataIndex: 'category', key: 'category' },
                        { title: 'Total Usage', dataIndex: 'usageInNumber', key: 'usage_in_number' },
                        { title: 'Total Usage %', dataIndex: 'usageInPercent', key: 'usage_in_percent' }
                    ]}
                    expandable={{
                        expandedRowRender: (records) => {                            
                            const totalBytes = records.apps.reduce( (prev, curr) => 
                                prev + (curr.dataTransferredInBytes ? curr.dataTransferredInBytes : 0)
                                ,  0
                            )
                            return (
                                <>
                                    <Row>
                                        <Col span={12}>
                                            <List
                                                header={<b>Websites and applications most visited</b>}
                                                bordered={false}
                                                split={false}
                                                size="small"
                                                itemLayout="vertical"                                                
                                            >
                                                {records.apps && records.apps.map(record => (
                                                    <List.Item key={record.applicationName}>                                                        
                                                        <AntTooltip title={humanFileSize(record.dataTransferredInBytes || 0)} placement="bottom">
                                                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                                <span>{record.applicationName}</span>
                                                                <Progress 
                                                                    percent={parseFloat((((record.dataTransferredInBytes || 0) / totalBytes) * 100).toFixed(0))} 
                                                                    style={{ width: '50%' }}
                                                                    status="active"
                                                                    showInfo={false}
                                                                    strokeColor="#FF8300"
                                                                />
                                                            </div> 
                                                        </AntTooltip>                                                                                                               
                                                    </List.Item>
                                                ))}                                        
                                            </List>
                                        </Col>
                                        <Col span={12}>
                                            <Table
                                                title={() => <b>{`Top networks data usage for ${records.category}`}</b>}
                                                rowKey={Math.random().toString(36).slice(2, 5)}
                                                size="small"
                                                bordered={false}
                                                columns={[
                                                    { title: 'SSID', dataIndex: 'networkSsid', key: 'networkSsid' },
                                                    { title: 'Site name', dataIndex: 'siteName', key: 'siteName' },
                                                    { title: 'Data transferred', dataIndex: 'totalBytesInHuman', key: 'totalBytesInHuman' }
                                                ]}
                                                dataSource={networkUsageByCate ? networkUsageByCate[records.category] : []}
                                            />
                                        </Col>
                                    </Row>
                                </>
                            );
                        },
                        expandIcon: ({expanded, onExpand, record}) =>
                            expanded ? (
                                <DownCircleTwoTone twoToneColor="#ff8300" onClick={e => onExpand(record, e)} />
                            ) : (
                                <RightCircleTwoTone twoToneColor="#ff8300" onClick={e => onExpand(record, e)} />
                            )
                        
                    }}
                    dataSource={appUsageData}
                    pagination={false}
                />
            </Card>
        </Space>
    )
}

export default ApplicationsPage;